import { observer } from 'mobx-react';
import IconButton, { ICON_BUTTON_ICONS, ICON_BUTTON_TYPES } from '../../buttons/IconButton';
import { PUNCTUATION } from '../../../utils/constants/notation';
import { getProfileIcon } from '../../../utils/utils';

/**
 * Build LinkedIn search URL from full name
 * Splits full_name on space to extract first/last name for search params
 * @param {string} fullName - Full name of the individual
 * @returns {string} LinkedIn search URL
 */
const buildLinkedInSearchUrl = (fullName) => {
  if (!fullName) return 'https://www.linkedin.com/search/results/people/';

  const nameParts = fullName.trim().split(/\s+/);
  const firstName = nameParts[0] || '';
  const lastName = nameParts.slice(1).join(' ') || '';

  const params = new URLSearchParams();
  if (firstName) params.set('firstName', firstName);
  if (lastName) params.set('lastName', lastName);

  return `https://www.linkedin.com/search/results/people/?${params.toString()}`;
};

/**
 * IndividualDetailsHeader - Header section with person info in blue border
 *
 * Displays:
 * - Gender icon (left)
 * - Full name
 * - Vertical divider
 * - Address label + formatted address
 * - Age label + age value
 * - Vertical divider
 * - LinkedIn search button (right)
 *
 * @param {Object} individual - Individual data
 */
const IndividualDetailsHeader = observer(({ individual = {} }) => {
  const fullName = individual.fullName || individual.full_name || PUNCTUATION.EMPTY_VALUE;
  const formattedAddress = individual.formattedAddress || PUNCTUATION.EMPTY_VALUE;
  // Fix #8: Use || for consistency - treats 0, '', null, undefined as empty
  const age = individual.age || PUNCTUATION.EMPTY_VALUE;

  const handleLinkedInClick = () => {
    window.open(buildLinkedInSearchUrl(fullName), '_blank');
  };

  return (
    <div className="individual-details-header">
      {/* Left section: Icon + Name */}
      <div className="header-left">
        <div className="profile-icon-wrap">
          <img className="profile-icon" src={getProfileIcon(individual.gender)} alt="Profile" />
        </div>
        <div className="name-text">{fullName}</div>
      </div>

      {/* Divider */}
      <div className="header-divider" />

      {/* Middle section: Address + Age */}
      <div className="header-middle">
        <div className="info-item address-item">
          <span className="info-label">Address</span>
          <span className="info-value">{formattedAddress}</span>
        </div>
        <div className="info-item age-item">
          <span className="info-label">Age</span>
          <span className="info-value">{age}</span>
        </div>
      </div>

      {/* Divider */}
      <div className="header-divider" />

      {/* Right section: Social links */}
      <div className="header-right">
        <IconButton
          icon={ICON_BUTTON_ICONS.LINKEDIN}
          type={ICON_BUTTON_TYPES.BLUE}
          onClick={handleLinkedInClick}
          tooltipText="Search on LinkedIn"
          wrapClassName="linkedin-btn"
        />
      </div>
    </div>
  );
});

export default IndividualDetailsHeader;
